// Copyright (c) Ping. All rights reserved.
//
// In-conversation message search. Opened from the chat header / Chat Info
// "Search" affordance: shows a search input pinned to the top of the chat and a
// results list scoped to the CURRENT conversation; tapping a result jumps to
// that message. Styling is NativeWind className-first per the repo styling rule.

import { Pressable, ScrollView, Text, TextInput, View } from 'react-native';

import { Search, X } from 'lucide-react-native';

// Web-only focus-outline reset — `outlineStyle` isn't a valid RN style key, so
// it's an inline web escape hatch (cast through `never` for the RN typings).
const INPUT_OUTLINE_RESET = { outlineStyle: 'none' } as never;

export interface InChatSearchResult {
  readonly id: string;
  readonly senderName: string;
  readonly snippet: string;
  readonly date: string;
}

export interface InChatSearchOverlayProps {
  readonly open: boolean;
  readonly query: string;
  readonly onQueryChange: (next: string) => void;
  readonly results: readonly InChatSearchResult[];
  readonly onPick: (messageId: string) => void;
  readonly onClose: () => void;
}

export function InChatSearchOverlay({
  open,
  query,
  onQueryChange,
  results,
  onPick,
  onClose,
}: InChatSearchOverlayProps): JSX.Element | null {
  if (!open) return null;
  const trimmed = query.trim();
  return (
    <View className="absolute left-0 right-0 top-0 z-50 px-3 pt-3">
      <View className="overflow-hidden rounded-xl border border-light-border-secondary bg-light-surface-panel shadow-2xl dark:border-[#2F333B] dark:bg-[#15171C]">
        {/* Search input row */}
        <View className="flex-row items-center gap-2 px-3 py-2">
          <Search size={18} color="#B5BAC3" />
          <TextInput
            value={query}
            onChangeText={onQueryChange}
            placeholder="Search in conversation"
            placeholderTextColor="#6B7280"
            autoFocus
            className="flex-1 text-[14px] text-light-text-primary dark:text-white"
            style={INPUT_OUTLINE_RESET}
          />
          <Pressable
            accessibilityRole="button"
            accessibilityLabel="Close search"
            onPress={onClose}
            className="h-7 w-7 items-center justify-center rounded-full active:opacity-80"
          >
            <X size={16} color="#B5BAC3" />
          </Pressable>
        </View>

        {/* Results */}
        {trimmed.length > 0 ? (
          <View className="border-t border-light-border-subtle dark:border-[#22242A]">
            {results.length === 0 ? (
              <Text className="px-3 py-3 text-[13px] text-light-text-secondary dark:text-dark-text-secondary">
                No messages found
              </Text>
            ) : (
              <ScrollView className="max-h-[320px]" keyboardShouldPersistTaps="handled">
                {results.map((r) => (
                  <Pressable
                    key={r.id}
                    accessibilityRole="button"
                    onPress={() => onPick(r.id)}
                    className="gap-0.5 px-3 py-2 active:opacity-80 hover:bg-light-surface-tertiary dark:hover:bg-white/[0.04]"
                  >
                    <View className="flex-row items-center justify-between">
                      <Text className="text-[13px] font-semibold text-light-text-primary dark:text-dark-text-primary">
                        {r.senderName}
                      </Text>
                      <Text className="text-[11px] text-light-text-tertiary dark:text-dark-text-tertiary">
                        {r.date}
                      </Text>
                    </View>
                    <Text
                      numberOfLines={1}
                      className="text-[13px] text-light-text-secondary dark:text-dark-text-secondary"
                    >
                      {r.snippet}
                    </Text>
                  </Pressable>
                ))}
              </ScrollView>
            )}
          </View>
        ) : null}
      </View>
    </View>
  );
}
